import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, IconButton, Paper, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Tabs, Tab, Tooltip, Divider, List, ListItem, ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import PreviewIcon from '@mui/icons-material/Preview';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import CodeIcon from '@mui/icons-material/Code';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import InfoIcon from '@mui/icons-material/Info';
import { useAdminFetch } from '../hooks/useAdminFetch';

interface TemplateVariable {
  name: string;
  description: string;
}

interface EmailTemplateListItem {
  template_name: string;
  name: string;
  description: string;
  has_custom: boolean;
  is_active: boolean;
  updated_at: string | null;
}

interface EmailTemplateDetail {
  template_name: string;
  name: string;
  description: string;
  variables: TemplateVariable[];
  subject: string;
  html_content: string;
  text_content: string;
  is_custom: boolean;
  is_active: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const AdminEmailHub: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Templates list state
  const [templates, setTemplates] = useState<EmailTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<EmailTemplateDetail | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Edit form state
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [textContent, setTextContent] = useState('');
  const [editTabValue, setEditTabValue] = useState(0);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Save/Test state
  const [saveLoading, setSaveLoading] = useState(false);
  const [testSendLoading, setTestSendLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reset confirmation
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Load templates list
  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminFetch(`${apiUrl}/api/admin/email-templates/`);
      setTemplates(data.templates);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setError(error.message || 'Failed to load templates');
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, adminFetch]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Handle edit template
  const handleEditTemplate = async (templateName: string) => {
    setEditLoading(true);
    setEditError(null);
    setEditDialogOpen(true);
    setEditTabValue(0);
    setPreviewHtml('');
    setPreviewText('');

    try {
      const data = await adminFetch(`${apiUrl}/api/admin/email-templates/${templateName}/`);
      setEditTemplate(data);
      setSubject(data.subject);
      setHtmlContent(data.html_content);
      setTextContent(data.text_content);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setEditError(error.message || 'Failed to load template');
      }
    } finally {
      setEditLoading(false);
    }
  };

  // Handle preview
  const handlePreview = async () => {
    if (!editTemplate) return;

    setPreviewLoading(true);
    setEditError(null);

    try {
      const data = await adminFetch(`${apiUrl}/api/admin/email-templates/${editTemplate.template_name}/preview/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html_content: htmlContent,
          text_content: textContent,
        }),
      });

      setPreviewHtml(data.html_preview);
      setPreviewText(data.text_preview);
      setEditTabValue(2); // Switch to preview tab
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setEditError(error.message || 'Failed to generate preview');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle test send
  const handleTestSend = async () => {
    if (!editTemplate) return;

    setTestSendLoading(true);
    setEditError(null);

    try {
      const data = await adminFetch(`${apiUrl}/api/admin/email-templates/${editTemplate.template_name}/test-send/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          html_content: htmlContent,
          text_content: textContent,
        }),
      });

      setSuccessMessage(data.message);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setEditError(error.message || 'Failed to send test email');
      }
    } finally {
      setTestSendLoading(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!editTemplate) return;

    setSaveLoading(true);
    setEditError(null);

    try {
      await adminFetch(`${apiUrl}/api/admin/email-templates/${editTemplate.template_name}/save/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          html_content: htmlContent,
          text_content: textContent,
        }),
      });

      setSuccessMessage('Template saved successfully!');
      setTimeout(() => setSuccessMessage(null), 4000);
      setEditDialogOpen(false);
      loadTemplates();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setEditError(error.message || 'Failed to save template');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle reset to default
  const handleResetToDefault = async () => {
    if (!editTemplate) return;

    setResetLoading(true);
    setEditError(null);

    try {
      await adminFetch(`${apiUrl}/api/admin/email-templates/${editTemplate.template_name}/reset/`, {
        method: 'DELETE',
      });

      setSuccessMessage('Template reset to default!');
      setTimeout(() => setSuccessMessage(null), 4000);
      setResetDialogOpen(false);
      setEditDialogOpen(false);
      loadTemplates();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setEditError(error.message || 'Failed to reset template');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Email Hub</Typography>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Manage email templates for your application. Click on a template to edit its content.
          Templates support Django template syntax with variables like {'{{ first_name }}'}.
        </Typography>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Template</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Modified</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No email templates found
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.template_name} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{template.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {template.template_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{template.description}</TableCell>
                  <TableCell>
                    {template.has_custom ? (
                      <Chip label="Custom" size="small" color="primary" />
                    ) : (
                      <Chip label="Default" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(template.updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Template">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditTemplate(template.template_name)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Template Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            Edit Template: {editTemplate?.name}
            {editTemplate?.is_custom && (
              <Chip label="Custom" size="small" color="primary" sx={{ ml: 1 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : editTemplate && (
            <>
              {editError && (
                <Alert severity="error" sx={{ mb: 2 }}>{editError}</Alert>
              )}

              {/* Variables Reference */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InfoIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">Available Variables</Typography>
                  <Typography variant="caption" color="text.secondary">(click to copy)</Typography>
                </Box>
                <List dense disablePadding>
                  {editTemplate.variables.map((variable) => (
                    <ListItem key={variable.name} disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Click to copy">
                              <Chip
                                label={`{{ ${variable.name} }}`}
                                size="small"
                                variant="outlined"
                                clickable
                                onClick={() => {
                                  const text = `{{ ${variable.name} }}`;
                                  navigator.clipboard.writeText(text);
                                  setSuccessMessage(`Copied: ${text}`);
                                  setTimeout(() => setSuccessMessage(null), 2000);
                                }}
                                sx={{
                                  fontFamily: 'monospace',
                                  cursor: 'pointer',
                                  '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                }}
                              />
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary">
                              {variable.description}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Subject */}
              <TextField
                label="Email Subject"
                fullWidth
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Divider sx={{ mb: 2 }} />

              {/* Tabs for HTML, Text, Preview */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={editTabValue} onChange={(_, v) => setEditTabValue(v)}>
                  <Tab icon={<CodeIcon />} iconPosition="start" label="HTML Version" />
                  <Tab icon={<TextFieldsIcon />} iconPosition="start" label="Plain Text Version" />
                  <Tab icon={<PreviewIcon />} iconPosition="start" label="Preview" />
                </Tabs>
              </Box>

              <TabPanel value={editTabValue} index={0}>
                <TextField
                  multiline
                  rows={16}
                  fullWidth
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="Enter HTML template..."
                  InputProps={{
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
                  }}
                />
              </TabPanel>

              <TabPanel value={editTabValue} index={1}>
                <TextField
                  multiline
                  rows={16}
                  fullWidth
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Enter plain text template..."
                  InputProps={{
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' }
                  }}
                />
              </TabPanel>

              <TabPanel value={editTabValue} index={2}>
                {previewHtml || previewText ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>HTML Preview:</Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: 'white',
                        maxHeight: 300,
                        overflow: 'auto'
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </Paper>

                    <Typography variant="subtitle2" gutterBottom>Plain Text Preview:</Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        backgroundColor: 'grey.100',
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                    >
                      {previewText}
                    </Paper>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    <PreviewIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography>
                      Click "Generate Preview" to see how the email will look
                    </Typography>
                  </Box>
                )}
              </TabPanel>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Box>
            {editTemplate?.is_custom && (
              <Button
                color="warning"
                startIcon={<RestoreIcon />}
                onClick={() => setResetDialogOpen(true)}
              >
                Reset to Default
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              startIcon={previewLoading ? <CircularProgress size={20} /> : <PreviewIcon />}
              onClick={handlePreview}
              disabled={previewLoading}
            >
              Generate Preview
            </Button>
            <Button
              variant="outlined"
              startIcon={testSendLoading ? <CircularProgress size={20} /> : <SendIcon />}
              onClick={handleTestSend}
              disabled={testSendLoading}
            >
              Send Test Email
            </Button>
            <Button
              variant="contained"
              startIcon={saveLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saveLoading}
            >
              Save Changes
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Template to Default?</DialogTitle>
        <DialogContent>
          <Typography>
            This will delete your custom template and restore the default template.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetToDefault}
            disabled={resetLoading}
            startIcon={resetLoading ? <CircularProgress size={20} /> : <RestoreIcon />}
          >
            Reset to Default
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEmailHub;
